import React, { useState } from 'react';
import { PostGroup, Post, UpdatePostData } from '../../services/postService';
import { useClient } from '../../contexts/ClientContext';
import { useToast } from '@/hooks/use-toast';
import postService from '../../services/postService';
import { X, Clock, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface UnscheduleModalProps {
  postGroup: PostGroup;
  onClose: () => void;
  onSuccess: () => void;
}

const PlatformIcon = ({ platform, className }) => {
  switch (platform) {
    case 'facebook':
      return <Facebook className={className} />;
    case 'instagram':
      return <Instagram className={className} />;
    case 'twitter':
      return <Twitter className={className} />;
    case 'linkedin':
      return <Linkedin className={className} />;
    default:
      return null;
  }
};

const UnscheduleModal: React.FC<UnscheduleModalProps> = ({ postGroup, onClose, onSuccess }) => {
  const { currentClient } = useClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [unscheduleAll, setUnscheduleAll] = useState(false);
  const [individualUnschedule, setIndividualUnschedule] = useState<{ [postId: string]: boolean }>({});

  const unscheduleMutation = useMutation({
    mutationFn: async () => {
      if (!currentClient) throw new Error('No client selected');

      let postsToUnschedule;

      if (unscheduleAll) {
        // When unscheduling all, only unschedule posts that are currently scheduled
        postsToUnschedule = postGroup.posts.filter(post => post.status === 'scheduled');
      } else {
        // When unscheduling individually, only unschedule selected posts that are scheduled
        postsToUnschedule = postGroup.posts.filter(post => 
          individualUnschedule[post.id] && post.status === 'scheduled'
        );
      }

      if (postsToUnschedule.length === 0) {
        throw new Error('No scheduled posts to unschedule');
      }

      // Update individual posts
      const updatePromises = postsToUnschedule.map(post => 
        postService.updatePost(post.id, {
          status: 'draft',
          scheduled_at: null,
        })
      );

      await Promise.all(updatePromises);

      // If unscheduling all scheduled posts, also update the post group if it's scheduled
      if (unscheduleAll && postGroup.status === 'scheduled') {
        await postService.updatePostGroup(postGroup.id, {
          status: 'draft',
          scheduled_at: null,
        });
      }

      return postsToUnschedule.length;
    },
    onSuccess: (count) => {
      toast({
        title: "Posts Unscheduled",
        description: `${count} post${count > 1 ? 's' : ''} have been unscheduled successfully.`,
      });
      
      // Invalidate all relevant queries to ensure calendar updates
      queryClient.invalidateQueries({ queryKey: ['scheduledPosts', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostGroups', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostsForQueue'] });
      queryClient.invalidateQueries({ queryKey: ['draftPosts'] });
      queryClient.invalidateQueries({ queryKey: ['postGroups'] });
      
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      console.error("Failed to unschedule posts:", error);
      if (error.message === 'No scheduled posts to unschedule') {
        toast({
          title: "No Changes",
          description: "No scheduled posts were found to unschedule.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Unscheduling Failed",
          description: "An error occurred while unscheduling the posts.",
          variant: "destructive",
        });
      }
    },
  });

  const handleUnschedule = () => {
    unscheduleMutation.mutate();
  };

  const handleIndividualUnscheduleChange = (postId: string, checked: boolean) => {
    setIndividualUnschedule(prev => ({ ...prev, [postId]: checked }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-slate-900/95 backdrop-blur-xl border-slate-200 dark:border-slate-600/50 w-full max-w-2xl">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold text-slate-900 dark:text-white">Unschedule Posts</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <Switch
              id="unschedule-all-switch"
              checked={unscheduleAll}
              onCheckedChange={setUnscheduleAll}
              className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-700"
            />
            <div className="flex-1">
              <label htmlFor="unschedule-all-switch" className="text-sm font-semibold text-slate-900 dark:text-slate-200 cursor-pointer">
                Unschedule all scheduled posts
              </label>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                Only scheduled posts will be unscheduled. Draft posts will remain unchanged.
              </p>
            </div>
          </div>

          <div className="space-y-4" style={{ opacity: unscheduleAll ? 0.5 : 1 }}>
            {postGroup.posts.map(post => (
              <div key={post.id} className={`border rounded-lg p-4 flex items-center justify-between ${
                post.status === 'scheduled' 
                  ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20' 
                  : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50'
              }`}>
                <div className="flex items-center space-x-3">
                  {post.platforms && post.platforms.length > 0 && (
                    <PlatformIcon platform={post.platforms[0]} className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{post.title}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        post.status === 'scheduled'
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600'
                      }`}>
                        {post.status === 'scheduled' ? 'Scheduled' : 'Draft'}
                      </span>
                    </div>
                    {post.status === 'scheduled' && post.scheduled_at && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 font-medium">
                        Scheduled for: {new Date(post.scheduled_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <Switch
                  checked={individualUnschedule[post.id] || false}
                  onCheckedChange={(checked) => handleIndividualUnscheduleChange(post.id, !!checked)}
                  disabled={unscheduleAll || post.status !== 'scheduled'}
                  className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-700"
                />
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="bg-white hover:bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUnschedule}
            disabled={unscheduleMutation.isPending}
            className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {unscheduleMutation.isPending ? 'Unscheduling...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnscheduleModal;